// Copyright (c) Ping. All rights reserved.
//
// Global minimized call card (PiP) — Figma Ping-Desktop 4121:11560
// ("call-in-progress-modal"). Shows realtime ongoing-call state: a header block
// with "Call in progress" + live timer, the call title + stacked participant
// avatars (max 3 + "+N"), and a controls row with mic + video split-buttons and
// a red hang-up. Tapping the header expands back to the full call view. The card
// is draggable anywhere — the DOM transform is updated directly during drag (no
// React re-render per move → smooth), committed on release and clamped to the
// viewport. Web-only. className-first; inline `style` only for the runtime drag
// position + per-avatar overlap offset.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Pressable, Text, View } from 'react-native';

import { ChevronDown, Mic, MicOff, PhoneOff, Video, VideoOff } from 'lucide-react-native';

import { useCallStore } from '@calls/store/callStore';

import { AnchoredPopover } from './AnchoredPopover';
import { DeviceMenu } from './DeviceMenu';
import { useCallDuration } from './useCallDuration';

// Figma frame: 323×108 (outer pad 4). Drag math clamps on the full footprint.
const CARD_W = 323;
const CARD_H = 108;
const MARGIN = 16;

function initials(name: string): string {
  const p = name.trim().split(/\s+/).filter(Boolean);
  if (p.length === 0) return '?';
  if (p.length === 1) return p[0]!.slice(0, 2).toUpperCase();
  return (p[0]![0]! + p[p.length - 1]![0]!).toUpperCase();
}

function clamp(v: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, v));
}

export function MinimizedCallCard(): JSX.Element {
  const active = useCallStore((s) => s.active);
  const participants = useCallStore((s) => s.participants);
  const micEnabled = useCallStore((s) => s.micEnabled);
  const cameraEnabled = useCallStore((s) => s.cameraEnabled);
  const connState = useCallStore((s) => s.connState);
  const duration = useCallDuration(active?.startedAt ?? null);

  const elRef = useRef<HTMLDivElement | null>(null);
  // `dx`/`dy` = pointer offset within the card; `sx`/`sy` = pointer-down origin
  // (to measure drag distance); `moved` = crossed the drag threshold.
  const dragRef = useRef<{
    dx: number;
    dy: number;
    sx: number;
    sy: number;
    moved: boolean;
  } | null>(null);
  // Set true the moment a gesture becomes a DRAG, so the header's tap-to-expand
  // (a click that fires AFTER pointerup) is suppressed — dragging to reposition
  // must never open the full call window. Reset on the next pointer-down.
  const suppressClickRef = useRef(false);
  // Below this many px of movement the gesture is a click, not a drag.
  const DRAG_THRESHOLD = 5;
  const [deviceMenu, setDeviceMenu] = useState<null | 'audioinput' | 'videoinput'>(null);
  // Anchors for the split-button caret device pickers.
   
  const micCaretRef = useRef<any>(null);
   
  const videoCaretRef = useRef<any>(null);
  // Initial position: bottom-right.
  const [pos, setPos] = useState(() => ({
    x: typeof window !== 'undefined' ? window.innerWidth - CARD_W - MARGIN : MARGIN,
    y: typeof window !== 'undefined' ? window.innerHeight - CARD_H - MARGIN : MARGIN,
  }));

  const onPointerMove = useCallback((e: PointerEvent) => {
    const d = dragRef.current;
    const el = elRef.current;
    if (!d || !el) return;
    // Only treat it as a drag once past the threshold — a still (or jittery)
    // press stays a click. Once a drag, suppress the follow-up expand click.
    if (!d.moved && Math.hypot(e.clientX - d.sx, e.clientY - d.sy) < DRAG_THRESHOLD) {
      return;
    }
    d.moved = true;
    suppressClickRef.current = true;
    const x = clamp(e.clientX - d.dx, MARGIN, window.innerWidth - CARD_W - MARGIN);
    const y = clamp(e.clientY - d.dy, MARGIN, window.innerHeight - CARD_H - MARGIN);
    // Direct DOM write during drag — no React churn.
    el.style.left = `${x}px`;
    el.style.top = `${y}px`;
  }, []);

  const onPointerUp = useCallback(() => {
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
    const el = elRef.current;
    if (el && dragRef.current?.moved) {
      setPos({ x: parseFloat(el.style.left), y: parseFloat(el.style.top) });
    }
    dragRef.current = null;
  }, [onPointerMove]);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      const el = elRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      // New gesture → assume a click until movement proves otherwise.
      suppressClickRef.current = false;
      dragRef.current = {
        dx: e.clientX - rect.left,
        dy: e.clientY - rect.top,
        sx: e.clientX,
        sy: e.clientY,
        moved: false,
      };
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', onPointerUp);
    },
    [onPointerMove, onPointerUp],
  );

  // Keep on-screen across viewport resizes.
  useEffect(() => {
    const onResize = () =>
      setPos((p) => ({
        x: clamp(p.x, MARGIN, window.innerWidth - CARD_W - MARGIN),
        y: clamp(p.y, MARGIN, window.innerHeight - CARD_H - MARGIN),
      }));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Cleanup any in-flight drag listeners on unmount.
  useEffect(
    () => () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    },
    [onPointerMove, onPointerUp],
  );

  if (!active) return <></>;

  const shown = participants.slice(0, 3);
  const extra = Math.max(0, participants.length - shown.length);
  // Tap the header to resume the full call — but NOT when the gesture was a
  // drag-to-reposition (the click fires after pointerup; the drag set the flag).
  const expand = (): void => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    useCallStore.getState().setMinimized(false);
  };

  return (
    <div
      ref={elRef}
      onPointerDown={onPointerDown}
      style={{
        position: 'fixed',
        left: pos.x,
        top: pos.y,
        width: CARD_W,
        zIndex: 2147483600,
        cursor: 'grab',
        touchAction: 'none',
        userSelect: 'none',
      }}
    >
      {/* Outer modal — Figma r12 p4 fill #111318 border #222429. */}
      <View className="gap-1 rounded-[12px] border border-[#E1E3E8] dark:border-[#222429] bg-white dark:bg-[#111318] p-1 shadow-lg">
        {/* Header block — r8 fill #15171C p8 gap4. Tap to expand the call. */}
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Expand call"
          onPress={expand}
          className="gap-1 rounded-[8px] bg-white dark:bg-[#15171C] p-2 active:opacity-90"
        >
          <View className="flex-row items-center justify-between">
            <Text className="font-sans text-[14px] font-bold leading-[20px] text-[#15171C] dark:text-white">
              Call in progress
            </Text>
            <Text className="font-sans text-[12px] leading-[17px] text-[#6B7280] dark:text-[#B5BAC3]">
              {connState === 'reconnecting' ? 'Reconnecting…' : duration}
            </Text>
          </View>
          <View className="flex-row items-center justify-between gap-2">
            <Text
              numberOfLines={1}
              className="min-w-0 flex-1 font-sans text-[14px] leading-[20px] text-[#6B7280] dark:text-[#B5BAC3]"
            >
              {active.title}
            </Text>
            {shown.length > 0 ? (
              <View className="flex-row items-center gap-0.5">
                <View className="flex-row items-center">
                  {shown.map((p, i) => (
                    <View
                      key={p.identity}
                      className="h-6 w-6 items-center justify-center overflow-hidden rounded-[6px] border-2 border-[#15171C] bg-[#3A3A3C]"
                      style={{ marginLeft: i === 0 ? 0 : -8 }}
                    >
                      <Text className="text-[9px] font-bold text-white">
                        {initials(p.name).slice(0, 2)}
                      </Text>
                    </View>
                  ))}
                </View>
                {extra > 0 ? (
                  <Text className="font-sans text-[12px] leading-[17px] text-[#6B7280] dark:text-[#B5BAC3]">
                    {`+${extra}`}
                  </Text>
                ) : null}
              </View>
            ) : null}
          </View>
        </Pressable>

        {/* Controls — p4 gap10. Mic + Video split-buttons (icon toggles, caret
            opens the device picker) and a red hang-up. Wrapped in a div that
            stops pointer/clicks from reaching the card's drag/expand handlers,
            so toggling a control NEVER expands to the full call. */}
        <div
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <View className="flex-row items-center gap-2.5 p-1">
            <View className="flex-row items-center gap-1">
              <SplitButton
                label={micEnabled ? 'Mute' : 'Unmute'}
                caretRef={micCaretRef}
                onPress={() => void useCallStore.getState().toggleMic()}
                onCaret={() => setDeviceMenu((m) => (m === 'audioinput' ? null : 'audioinput'))}
              >
                {micEnabled ? (
                  <Mic size={20} strokeWidth={1.5} color="#8C929E" />
                ) : (
                  <MicOff size={20} strokeWidth={1.5} color="#FF8A8A" />
                )}
              </SplitButton>
              <SplitButton
                label={cameraEnabled ? 'Stop video' : 'Start video'}
                caretRef={videoCaretRef}
                onPress={() => void useCallStore.getState().toggleCamera()}
                onCaret={() => setDeviceMenu((m) => (m === 'videoinput' ? null : 'videoinput'))}
              >
                {cameraEnabled ? (
                  <Video size={20} strokeWidth={1.5} color="#8C929E" />
                ) : (
                  <VideoOff size={20} strokeWidth={1.5} color="#8C929E" />
                )}
              </SplitButton>
            </View>
            <View className="flex-1" />
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Leave call"
              onPress={() => void useCallStore.getState().endCall()}
              className="h-7 w-7 items-center justify-center rounded-[8px] active:opacity-70"
            >
              <PhoneOff size={20} strokeWidth={1.5} color="#FF5353" />
            </Pressable>
          </View>
        </div>
      </View>

      {/* Device pickers anchored to the split-button carets. */}
      <AnchoredPopover
        anchorRef={micCaretRef}
        open={deviceMenu === 'audioinput'}
        onClose={() => setDeviceMenu(null)}
      >
        <DeviceMenu kind="audioinput" title="Microphone" onClose={() => setDeviceMenu(null)} />
      </AnchoredPopover>
      <AnchoredPopover
        anchorRef={videoCaretRef}
        open={deviceMenu === 'videoinput'}
        onClose={() => setDeviceMenu(null)}
      >
        <DeviceMenu kind="videoinput" title="Camera" onClose={() => setDeviceMenu(null)} />
      </AnchoredPopover>
    </div>
  );
}

/** A mic/video control: 28px icon button (rounded-l) + a caret (rounded-r). */
function SplitButton({
  label,
  onPress,
  onCaret,
  caretRef,
  children,
}: {
  label: string;
  onPress: () => void;
  onCaret: () => void;
   
  caretRef: React.MutableRefObject<any>;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <View className="flex-row items-center gap-px">
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={label}
        onPress={onPress}
        className="h-7 w-7 items-center justify-center rounded-l-[8px] rounded-r-[2px] active:opacity-70"
      >
        {children}
      </Pressable>
      <Pressable
        ref={caretRef}
        accessibilityRole="button"
        accessibilityLabel={`${label} options`}
        onPress={onCaret}
        className="h-7 w-[14px] items-center justify-center rounded-l-[2px] rounded-r-[8px] active:opacity-70"
      >
        <ChevronDown size={10} strokeWidth={2} color="#8C929E" />
      </Pressable>
    </View>
  );
}
